import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { Button, IconButton } from "@hortis/ui/button";
import { Dialog } from "@hortis/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHandle,
  DrawerRemoveScroll,
} from "@hortis/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { HoverCard, HoverCardTrigger } from "@hortis/ui/hover-card";
import {
  AlignLeft1,
  CameraPlus,
  DotsHorizontal,
  ExternalLink1,
  Eye,
  EyeOff,
  Folder,
  GitBranch1,
  RefreshCw5,
  Tag1,
  Target1,
  Trash3,
} from "@hortis/ui/icons";
import { MarkerPinOff1 } from "@hortis/ui/icons/marker-pin-off-1";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { RichTextViewer } from "@hortis/ui/rich-text-editor";
import { ScrollArea, ScrollAreaViewport } from "@hortis/ui/scroll-area";
import { Sheet, SheetContent } from "@hortis/ui/sheet";
import { Skeleton } from "@hortis/ui/skeleton";
import { Tag } from "@hortis/ui/tag";
import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { MaterialGroup, PlantMaterialStatus } from "generated/graphql";
import { useState } from "react";
import { AccessionMaterialStatusSummary } from "src/components/accession-material-status-summary/accession-material-status-summary";
import { RestoreMaterialDialogContent } from "src/components/dialog/variants/restore-material";
import { TrashMaterialDialogContent } from "src/components/dialog/variants/trash-material";
import { MakeObservationDialogContent } from "src/components/modals/make-observation";
import {
  TopBar,
  TopBarActionButtons,
  TopBarContent,
} from "src/components/nav-layout/topbar";
import {
  RecordPageHeader,
  RecordPageHeaderContent,
  RecordPageHeaderPropertiesItem,
  RecordPageHeaderPropertiesSummary,
  RecordPageHeaderReference,
  RecordPageHeaderReferenceButton,
  RecordPageHeaderSubtitle,
  RecordPageHeaderThumbnail,
  RecordPageHeaderTitle,
} from "src/components/record-page-header";
import { mentionReadonly } from "src/components/rich-text-editor/mention";
import { AccessionHoverCardContent } from "src/components/rich-text-editor/mention/accession-hover-card-content";
import { TaxonHoverCardContent } from "src/components/rich-text-editor/mention/taxon-hover-card-content";
import { useFindOnMap } from "src/features/collection-map/hooks";
import { useMapContext } from "src/features/collection-map/map-store";
import { mapId } from "src/features/collection-map/utils";
import {
  createMaterialNumber,
  parseMaterialNumber,
} from "src/features/collection/components/plant-materials/material-number";
import { CreatePhotosDialog } from "src/features/material-page/create-photos-dialog/create-photos-dialog";
import { MakePrivateDialog } from "src/features/material-page/header/make-private-dialog";
import { MakePublicDialog } from "src/features/material-page/header/make-public-dialog";
import { MaterialCopyDropdownMenuSub } from "src/features/material-page/header/material-copy-dropdown-menu-sub";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { ActivityItem } from "src/features/records/components/record-activity/activity-item";
import { parseMaterialHistory } from "src/features/records/components/record-activity/parse-material-history";
import { EditMaterialSheetForm } from "src/features/records/material/components/edit-drawer/edit-details";
import {
  HierarchyContent,
  HierarchyListGroup,
  HierarchyPopover,
  HierarchyTrigger,
} from "src/features/records/material/components/hierarchy-popover";
import HierarchyListItem from "src/features/records/material/components/hierarchy-popover/hierachy-list-item";
import { getMaterialDocument } from "src/features/records/material/graphql";
import { MaterialLastObservationSection } from "src/features/records/material/sections/material-last-observation-section";
import { MaterialMobileProperties } from "src/features/records/material/sections/material-properties";
import { PublicRecordBanner } from "src/features/records/material/sections/public-record-banner";
import { RecordGallerySection } from "src/features/taxonomy/components/taxon-page/sections/record-gallery-section";
import { useCollectionSiteSettings } from "src/utils/hooks/collection-site-from-url";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useDateStringFormatter } from "src/utils/hooks/language";
import { useMobile } from "src/utils/hooks/media-query";
import { useIsScrolled } from "src/utils/hooks/scrolled";
import { getThumbnailPhoto } from "src/utils/photos";
import { twMerge } from "tailwind-merge";
import { MaterialTrashRecordAlert } from "../../material/$materialNumber";

export const Route = createFileRoute(
  "/_layout/site/$siteSlug/map/material/$materialNumber",
)({
  component: Component,
});

const extensions = [mentionReadonly];

function Component() {
  const { requestEdit, collapseSidebar, sidebarOpen } = useMapContext();
  const { siteSlug, materialNumber } = Route.useParams();
  const { subdomain } = Route.useRouteContext();
  const [snap, setSnap] = useState<number | string | null>("224px");
  const findOnMap = useFindOnMap(mapId);
  const isMobile = useMobile({ defaultMatches: true });
  const { attachRef } = useIsScrolled();
  const { canEdit } = useAccessRole();
  const [observationModalOpen, setObservationModalOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [makePublicDialogOpen, setMakePublicDialogOpen] = useState(false);
  const [makePrivateDialogOpen, setMakePrivateDialogOpen] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const [uploadPhotoDialogOpen, setUploadPhotoDialogOpen] = useState(false);
  const [createPhotosDialogOpen, setCreatePhotosDialogOpen] = useState(false);
  const { isPublic: isSitePublic } = useCollectionSiteSettings();
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const formatDateString = useDateStringFormatter();

  const materialNumberParts = parseMaterialNumber(materialNumber);

  const { data, refetch: refetchMaterial } = useGqlQuery({
    query: getMaterialDocument,
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      accessionNumber: materialNumberParts?.accessionNumber ?? "",
      qualifier: materialNumberParts?.qualifier ?? "",
    },
    enabled: materialNumberParts != null,
    placeholderData: keepPreviousData,
  });

  let material = data?.org?.site?.result ?? undefined;
  const isPublic = material?.public ?? false;

  const isPresentPlant =
    material?.status === PlantMaterialStatus.Present &&
    material.materialGroup === MaterialGroup.Plant;

  const navigate = Route.useNavigate();

  const recordHeaderButtons =
    material == null ? (
      <Skeleton className="h-6 w-48" />
    ) : (
      <>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              data-cy="action-menu-button"
              icon={DotsHorizontal}
              variant="tertiaryGray"
              size="sm"
              className="p-1 pointer-events-auto"
              ariaLabel="More actions"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="pointer-events-auto">
            {material.accession != null && isMobile ? (
              <DropdownMenuItem asChild testId="go-to-accession-menu-item">
                <Link
                  to="/site/$siteSlug/accession/$accessionNumber"
                  from="/site/$siteSlug"
                  params={{
                    accessionNumber: material.accession.accessionNumber,
                  }}
                >
                  <ExternalLink1 className="h-4 w-4 text-grey-500" />
                  Open accession
                </Link>
              </DropdownMenuItem>
            ) : null}
            {material.accession?.taxon != null && isMobile ? (
              <>
                <DropdownMenuItem asChild testId="go-to-taxon-menu-item">
                  <Link
                    to="/taxon/$taxonId"
                    params={{ taxonId: material.accession.taxon.id }}
                  >
                    <ExternalLink1 className="h-4 w-4 text-grey-500" />
                    Open taxon
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            ) : null}
            <DropdownMenuGroup>
              {canEdit && (
                <>
                  <DropdownMenuItem
                    data-cy="add-photos-record-menu-item"
                    onSelect={() => {
                      setCreatePhotosDialogOpen(true);
                    }}
                  >
                    <CameraPlus className="h-4 w-4 text-grey-500" />
                    Add photos
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {material.position != null && (
                <DropdownMenuItem
                  onClick={() => {
                    findOnMap(material.position);
                  }}
                  data-cy="menu-option-find-on-map"
                >
                  <Target1 className="h-4 w-4 text-grey-500" />
                  Show on map
                </DropdownMenuItem>
              )}
              {material.accession != null && (
                <DropdownMenuItem asChild>
                  <Link
                    from="/site/$siteSlug/map"
                    to="/site/$siteSlug/material/$materialNumber"
                    params={{
                      materialNumber: createMaterialNumber(
                        material.accession.accessionNumber,
                        material.qualifier,
                      ),
                    }}
                  >
                    <ExternalLink1 className="h-4 w-4 text-grey-500" />
                    Open record
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <MaterialCopyDropdownMenuSub material={material} />
            {canEdit && (
              <>
                {isPresentPlant && !material.archived && isSitePublic && (
                  <>
                    <DropdownMenuGroup>
                      {isPublic && (
                        <DropdownMenuItem
                          data-cy="make-private-menu-item"
                          onClick={() => {
                            setMakePrivateDialogOpen(true);
                          }}
                        >
                          <EyeOff className="h-4 w-4 text-grey-500" />
                          Make private
                        </DropdownMenuItem>
                      )}

                      {!isPublic && (
                        <DropdownMenuItem
                          data-cy="make-public-menu-item"
                          onClick={() => {
                            setMakePublicDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 text-grey-500" />
                          Make public
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuGroup>
                  {!material.archived && (
                    <DropdownMenuItem
                      onClick={() => {
                        setTrashDialogOpen(true);
                      }}
                      testId="archive-material-menu-item"
                    >
                      <Trash3 className="h-4 w-4 text-grey-500" />
                      Delete
                    </DropdownMenuItem>
                  )}
                  {material.archived && (
                    <DropdownMenuItem
                      onClick={() => {
                        setRestoreDialogOpen(true);
                      }}
                      testId="restore-material-menu-item"
                    >
                      <RefreshCw5 className="h-4 w-4 text-grey-500" />
                      Restore
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Dialog
          open={observationModalOpen}
          onOpenChange={setObservationModalOpen}
        >
          <MakeObservationDialogContent
            onSubmit={() => {
              void refetchMaterial();
              setObservationModalOpen(false);
            }}
            onClose={() => {
              setObservationModalOpen(false);
            }}
            materialId={material.id}
            defaultValues={material}
          />
        </Dialog>
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <RestoreMaterialDialogContent
            onSuccess={() => {
              setRestoreDialogOpen(false);
            }}
            id={material.id}
          />
        </Dialog>
        <CreatePhotosDialog
          materialId={material.id}
          open={createPhotosDialogOpen}
          onOpenChange={setCreatePhotosDialogOpen}
        />
      </>
    );

  const menuContent =
    material == null ? (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-1 px-4 lg:px-6">
          <Skeleton className="h-7 rounded-full w-24" />
          <Skeleton className="h-7 rounded-full w-32" />
          <Skeleton className="h-7 rounded-full w-20" />
          <Skeleton className="h-7 rounded-full w-16" />
          <Skeleton className="h-7 rounded-full w-32" />
          <Skeleton className="h-7 rounded-full w-16" />
          <Skeleton className="h-7 rounded-full w-24" />
          <Skeleton className="h-7 rounded-full w-20" />
        </div>
        <div className="border-y border-grey-200 px-4 lg:px-6 py-5 space-y-5">
          <Skeleton className="h-6 w-32" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-5 w-5/6" />
            <Skeleton className="h-5 w-4/6" />
          </div>
        </div>
        <div className="px-4 lg:px-6 space-y-5">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-5/6" />
            <Skeleton className="h-5 w-6/6" />
            <Skeleton className="h-5 w-4/6" />
          </div>
        </div>
        <div className="px-4 lg:px-6 space-y-5 pt-4">
          <Skeleton className="h-6 w-32" />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-3/6" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-2/6" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-1/6" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-2/6" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-3/6" />
            </div>
          </div>
        </div>
      </div>
    ) : (
      <>
        <div className="text-base flex flex-col gap-4 pb-4">
          <MaterialMobileProperties
            className="px-4 lg:px-6"
            material={material}
            onRequestEdit={() => {
              setEditDrawerOpen(true);
            }}
            onRequestResposition={() => {
              requestEdit(material);
            }}
            canEdit={canEdit}
          />
          {material.materialGroup === MaterialGroup.Plant && (
            <MaterialLastObservationSection
              material={material}
              className="shadow-none border-x-0 rounded-none"
              compact
            />
          )}
          {material.notes != null && material.notes !== "" && (
            <div className="space-y-2 pb-1 px-4 lg:px-6">
              <div className="font-medium text-grey-900 flex items-center justify-between">
                Notes{" "}
              </div>
              <RichTextViewer
                value={material.notesData}
                extensions={extensions}
                key={JSON.stringify(material.notesData)}
              />
            </div>
          )}
          <div className="space-y-3 px-4 lg:px-6">
            <div className="text-grey-900 font-medium">Activity</div>
            <div>
              {material?.historyConnection?.nodes == null
                ? null
                : parseMaterialHistory(
                    material.historyConnection.nodes,
                    formatDateString,
                    undefined,
                  ).map((props, i) => <ActivityItem key={i} {...props} />)}
            </div>
          </div>
        </div>

        <Sheet open={editDrawerOpen} onOpenChange={setEditDrawerOpen}>
          <SheetContent>
            <EditMaterialSheetForm
              onSubmit={refetchMaterial}
              material={material}
              open={editDrawerOpen}
              requestClose={() => {
                setEditDrawerOpen(false);
              }}
              hideObservationFields
              hidePositionFields
            />
          </SheetContent>
        </Sheet>

        {canEdit && (
          <>
            <Dialog open={trashDialogOpen} onOpenChange={setTrashDialogOpen}>
              <TrashMaterialDialogContent
                id={material.id}
                onSuccess={() => {
                  setTrashDialogOpen(false);
                  void refetchMaterial();
                }}
              />
            </Dialog>
            <MakePrivateDialog
              open={makePrivateDialogOpen}
              onOpenChange={setMakePrivateDialogOpen}
              id={material.id}
              onSuccess={refetchMaterial}
            />
            <MakePublicDialog
              open={makePublicDialogOpen}
              onOpenChange={setMakePublicDialogOpen}
              id={material.id}
              onSuccess={refetchMaterial}
            />
          </>
        )}
      </>
    );

  return isMobile ? (
    <Drawer
      key={String(open)}
      open
      onOpenChange={(open) => {
        if (!open) {
          void navigate({
            from: "/site/$siteSlug/map/material/$materialNumber",
            to: "/site/$siteSlug/map",
          });
        }
      }}
      modal={false}
      shouldScaleBackground={false}
      snapPoints={["224px", 1]}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      scrollLockTimeout={50}
    >
      <DrawerContent
        className={twMerge(
          "h-full max-h-full shadow-md-up overflow-hidden",
          snap === 1 && "rounded-t-none",
        )}
        onInteractOutside={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {material?.public === true ? (
          <PublicRecordBanner compact material={material} className="px-4 mb-4">
            <DrawerHandle className="bg-primary-200 absolute left-1/2 -translate-x-1/2" />
          </PublicRecordBanner>
        ) : material?.archived === true ? (
          <MaterialTrashRecordAlert
            materialId={material.id}
            canEdit={canEdit}
            compact
            className="mb-4"
          >
            <DrawerHandle className="absolute left-1/2 -translate-x-1/2" />
          </MaterialTrashRecordAlert>
        ) : (
          <DrawerHandle />
        )}
        <DrawerRemoveScroll
          className={twMerge(
            "h-full pb-4",
            snap === 1 && "overflow-y-scroll",
            snap !== 1 && "overflow-clip",
          )}
        >
          <div ref={attachRef} className="flex w-full flex-col bg-white">
            <div className="flex flex-col gap-2 pb-4">
              {material == null ? (
                <div className="px-4 flex items-center gap-3 pt-2">
                  <Skeleton className="h-[68px] w-[68px] rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                </div>
              ) : (
                <>
                  <RecordPageHeader className="px-4 lg:px-6">
                    <RecordPageHeaderThumbnail
                      thumbnail={material.images?.[0] ?? null}
                      additionalPhotos={material.images ?? []}
                      grayscale={
                        material.status === PlantMaterialStatus.Absent ||
                        material.archived
                      }
                      className="h-[68px] w-[68px] shrink-0"
                    />
                    <RecordPageHeaderContent className="gap-0">
                      <RecordPageHeaderPropertiesSummary>
                        <RecordPageHeaderPropertiesItem className="flex-1">
                          {createMaterialNumber(
                            material.accession?.accessionNumber ?? "",
                            material.qualifier,
                            "/",
                          )}
                        </RecordPageHeaderPropertiesItem>
                        <div className="flex items-center gap-1">
                          {material.accession?.taxon?.sharedScientificName
                            ?.taxon?.globalRedListStatus != null &&
                            material.accession.taxon.fullyValidated ===
                              true && (
                              <IUCNRedListTag
                                status={
                                  material.accession?.taxon.sharedScientificName
                                    ?.taxon?.globalRedListStatus
                                }
                                short
                                className="text-xs py-px px-1 rounded-md"
                              />
                            )}
                          <Tag className="border-grey-100 bg-grey-100 text-xs py-px px-1 rounded-md">
                            {material.accession?.provenanceCategory?.codeId}
                          </Tag>
                        </div>
                      </RecordPageHeaderPropertiesSummary>
                      <RecordPageHeaderTitle className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            {material.accession?.scientificName ?? "-"}
                          </div>
                          {recordHeaderButtons}
                          {material.position == null && (
                            <MarkerPinOff1 className="h-4 w-4 shrink-0 text-grey-500" />
                          )}
                        </div>
                      </RecordPageHeaderTitle>
                      {material.accession?.taxon?.commonName != null && (
                        <RecordPageHeaderSubtitle className="text-base font-medium text-grey-500">
                          {material.accession.taxon.commonName}
                        </RecordPageHeaderSubtitle>
                      )}
                    </RecordPageHeaderContent>
                  </RecordPageHeader>
                </>
              )}
            </div>
            {menuContent}
          </div>
        </DrawerRemoveScroll>
      </DrawerContent>
    </Drawer>
  ) : (
    <div
      key={material?.id}
      className={twMerge(
        "pointer-events-auto z-50 flex h-full w-full max-w-xs flex-col self-center bg-white shadow-md lg:max-w-md",
        !sidebarOpen && "hidden",
      )}
    >
      <TopBar>
        <TopBarContent className="gap-3">
          {material == null ? (
            <Skeleton className="h-5 w-48" />
          ) : (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Button asChild variant="linkgray">
                    <Link
                      from="/site/$siteSlug/map/material/$materialNumber"
                      to="/site/$siteSlug/map"
                    >
                      Map
                    </Link>
                  </Button>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem className="-mx-1">
                  <HierarchyPopover>
                    <HierarchyTrigger>
                      {material.accession?.accessionNumber}/{material.qualifier}
                    </HierarchyTrigger>
                    <HierarchyContent>
                      <HierarchyListItem
                        root
                        to="/taxon/$taxonId"
                        params={{ taxonId: material.accession?.taxon?.id }}
                      >
                        <GitBranch1 className="h-4 w-4 text-grey-500" />
                        <span className="text-sm font-medium text-grey-900">
                          {material.accession?.taxon?.scientificName ?? "-"}
                        </span>
                      </HierarchyListItem>
                      <HierarchyListGroup indent>
                        <HierarchyListItem
                          to="/site/$siteSlug/accession/$accessionNumber"
                          params={{
                            accessionNumber:
                              material.accession?.accessionNumber,
                          }}
                        >
                          <Folder className="h-4 w-4 text-grey-500" />
                          <span className="text-sm font-medium text-grey-900">
                            {material.accession?.accessionNumber ?? "-"}
                          </span>
                        </HierarchyListItem>
                        <HierarchyListGroup indent>
                          {material.accession?.plantMaterial?.map(
                            (plantMaterial) => {
                              const selected = plantMaterial.id === material.id;
                              return (
                                <HierarchyListItem
                                  to="/site/$siteSlug/map/material/$materialNumber"
                                  params={{
                                    materialNumber: createMaterialNumber(
                                      // Optional chained in above map
                                      material.accession?.accessionNumber ?? "",
                                      plantMaterial.qualifier,
                                    ),
                                  }}
                                  selected={selected}
                                  key={plantMaterial.id}
                                >
                                  <Tag1
                                    className={twMerge(
                                      "h-4 w-4 text-grey-500",
                                      selected && "text-primary-500",
                                    )}
                                  />
                                  <span
                                    className={twMerge(
                                      "text-sm font-medium text-grey-900",
                                      selected && "text-primary-500",
                                    )}
                                  >
                                    {`Material ${plantMaterial.qualifier}`}
                                  </span>
                                </HierarchyListItem>
                              );
                            },
                          )}
                        </HierarchyListGroup>
                      </HierarchyListGroup>
                    </HierarchyContent>
                  </HierarchyPopover>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </TopBarContent>
        <TopBarActionButtons>
          <IconButton
            size="sm"
            variant="tertiaryGray"
            onClick={() => {
              collapseSidebar();
            }}
            icon={AlignLeft1}
            ariaLabel="Collapse material details"
            testId="material-details-collapse-button"
            className="bg-opacity-0 hover:bg-opacity-50"
          />
        </TopBarActionButtons>
      </TopBar>
      <ScrollArea>
        <ScrollAreaViewport
          ref={attachRef}
          className="flex flex-col gap-4 bg-white max-w-full"
        >
          {material == null ? (
            <div className="px-6 pt-6">
              <div className="grid grid-cols-3 gap-2">
                <Skeleton className="h-full w-full aspect-16/10 col-span-2 rounded-xl" />
                <div className="space-y-2">
                  <Skeleton className="w-full aspect-16/10 rounded-xl" />
                  <Skeleton className="w-full aspect-16/10 rounded-xl" />
                </div>
              </div>
              <div className="my-4 space-y-2">
                <Skeleton className="h-5 w-32" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-44" />
                </div>
              </div>
            </div>
          ) : (
            <>
              <PublicRecordBanner compact material={material} />
              {material.archived && (
                <MaterialTrashRecordAlert
                  materialId={material.id}
                  canEdit={canEdit}
                  compact
                />
              )}
              <RecordGallerySection
                className="px-6 pt-6"
                mapLocations={
                  material.position == null ? [] : [material.position]
                }
                thumbnail={getThumbnailPhoto(material)}
                photos={material.images ?? []}
                hideMap
                grayscale={
                  material.status === PlantMaterialStatus.Absent ||
                  material.archived
                }
                materialId={material.id}
                canEdit={canEdit}
              />
              <RecordPageHeader className="px-6 py-4">
                <RecordPageHeaderContent>
                  <RecordPageHeaderTitle>
                    <div className="flex items-center gap-2">
                      {createMaterialNumber(
                        material.accession?.accessionNumber ?? "",
                        material.qualifier,
                        "/",
                      )}
                      {material.position == null && (
                        <MarkerPinOff1 className="h-4 w-4 shrink-0 text-grey-500" />
                      )}
                      {recordHeaderButtons}
                    </div>
                  </RecordPageHeaderTitle>
                  <div className="pt-1">
                    {material.accession?.taxon != null && (
                      <RecordPageHeaderReference>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <RecordPageHeaderReferenceButton asChild>
                              <Link
                                to="/taxon/$taxonId"
                                params={{
                                  taxonId: material.accession.taxon.id,
                                }}
                              >
                                {material.accession?.taxon.sharedScientificName
                                  ?.taxon?.globalRedListStatus != null &&
                                material.accession.taxon.fullyValidated ===
                                  true ? (
                                  <IUCNRedListTag
                                    status={
                                      material.accession?.taxon
                                        .sharedScientificName?.taxon
                                        ?.globalRedListStatus
                                    }
                                    short
                                    className="text-xs py-px px-1 rounded-md"
                                  />
                                ) : (
                                  <GitBranch1 className="h-4 w-4 text-grey-500" />
                                )}
                                {material.accession?.scientificName}
                                <span className="text-grey-500">
                                  {material.accession?.taxon?.commonName}
                                </span>
                              </Link>
                            </RecordPageHeaderReferenceButton>
                          </HoverCardTrigger>
                          <TaxonHoverCardContent
                            taxon={material.accession.taxon}
                          />
                        </HoverCard>
                      </RecordPageHeaderReference>
                    )}
                    {material.accession != null && (
                      <RecordPageHeaderReference>
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <RecordPageHeaderReferenceButton asChild>
                              <Link
                                to="/accession/$id"
                                params={{ id: material.accession.id }}
                              >
                                <Tag className="border-grey-100 bg-grey-100 text-xs py-px px-1 rounded-md">
                                  {
                                    material.accession.provenanceCategory
                                      ?.codeId
                                  }
                                </Tag>
                                {/* <Folder className="h-4 w-4 text-grey-500" /> */}
                                {material.accession.accessionNumber}
                                <AccessionMaterialStatusSummary
                                  materials={
                                    material.accession?.plantMaterial ?? []
                                  }
                                />
                              </Link>
                            </RecordPageHeaderReferenceButton>
                          </HoverCardTrigger>
                          <AccessionHoverCardContent
                            accession={material.accession}
                          />
                        </HoverCard>
                      </RecordPageHeaderReference>
                    )}
                  </div>
                </RecordPageHeaderContent>
              </RecordPageHeader>
            </>
          )}
          {menuContent}
        </ScrollAreaViewport>
      </ScrollArea>
    </div>
  );
}
